<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      @click.self="close"
      @keydown.esc="close"
    >
      <div
        class="bg-white rounded-xl shadow-2xl w-full max-w-[600px] flex flex-col"
        style="height: 80vh; max-height: 720px;"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ask-ai-title"
      >
        <header class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <Icon name="mdi-message-text-outline" class="w-5 h-5 text-primary-600" />
            <h2 id="ask-ai-title" class="font-semibold text-gray-900">Ask CIM-PRO</h2>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="messages.length > 1"
              type="button"
              class="text-xs text-gray-500 hover:text-gray-700"
              :disabled="isPending"
              @click="onClear"
            >
              Clear conversation
            </button>
            <button
              type="button"
              class="text-gray-500 hover:text-gray-700"
              aria-label="Close"
              @click="close"
            >
              <Icon name="mdi-close" class="w-5 h-5" />
            </button>
          </div>
        </header>

        <div
          ref="scrollContainer"
          class="flex-1 overflow-y-auto px-4 py-3"
        >
          <TransitionGroup
            tag="div"
            class="space-y-3"
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
          >
            <div v-for="(msg, i) in messages" :key="`${i}-${msg.createdAt}`">
            <!-- Greeting -->
            <div
              v-if="msg.role === 'ai' && msg.status === 'greeting'"
              class="flex items-start gap-2"
            >
              <img src="/favicon.ico" alt="Cimpro AI" class="w-7 h-7 rounded-full shrink-0 mt-0.5">
              <div class="space-y-2">
                <div class="text-sm text-gray-700 bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 inline-block">
                  Hi there! What are you looking for?
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    size="small"
                    severity="secondary"
                    outlined
                    :disabled="isPending || direction !== null"
                    @click="onPickInitial('businesses')"
                  >
                    Businesses
                  </Button>
                  <Button
                    size="small"
                    severity="secondary"
                    outlined
                    :disabled="isPending || direction !== null"
                    @click="onPickInitial('contacts')"
                  >
                    Contacts
                  </Button>
                </div>
              </div>
            </div>

            <!-- v-else preserves existing chain -->
            <!-- User bubble -->
            <div
              v-if="msg.role === 'user'"
              class="flex justify-end items-start gap-2"
            >
              <div class="max-w-[80%] bg-primary-100 text-gray-900 rounded-lg rounded-tr-none px-3 py-2 text-sm whitespace-pre-wrap">
                {{ msg.text }}
              </div>
              <div class="w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-medium flex items-center justify-center shrink-0 mt-0.5">
                {{ userInitials }}
              </div>
            </div>

            <!-- Typing -->
            <div
              v-else-if="msg.status === 'typing'"
              class="flex items-start gap-2"
            >
              <img src="/favicon.ico" alt="Cimpro AI" class="w-7 h-7 rounded-full shrink-0 mt-0.5">
              <div class="flex items-center gap-1 bg-gray-100 rounded-lg rounded-tl-none px-3 py-3">
                <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms" />
                <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms" />
                <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms" />
              </div>
            </div>

            <!-- Searching -->
            <div
              v-else-if="msg.status === 'searching'"
              class="flex items-start gap-2"
            >
              <img src="/favicon.ico" alt="Cimpro AI" class="w-7 h-7 rounded-full shrink-0 mt-0.5">
              <div class="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 rounded-lg rounded-tl-none px-3 py-2">
                <Icon name="mdi-loading" class="w-4 h-4 animate-spin" />
                <span>{{ msg.text }}</span>
              </div>
            </div>

            <!-- Empty / error -->
            <div
              v-else-if="msg.status === 'empty' || msg.status === 'error'"
              class="flex items-start gap-2"
            >
              <img src="/favicon.ico" alt="Cimpro AI" class="w-7 h-7 rounded-full shrink-0 mt-0.5">
              <div
                class="text-sm bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 inline-block"
                :class="msg.status === 'error' ? 'text-red-600' : 'text-gray-700'"
              >
                {{ msg.text }}
              </div>
            </div>

            <!-- Results -->
            <div v-else-if="msg.status === 'results'" class="flex items-start gap-2">
              <img src="/favicon.ico" alt="Cimpro AI" class="w-7 h-7 rounded-full shrink-0 mt-0.5">
              <div class="flex-1 min-w-0 space-y-2">
                <div class="flex items-baseline justify-between gap-2">
                  <div class="text-sm text-gray-700 bg-gray-100 rounded-lg rounded-tl-none px-3 py-2">{{ msg.introText }}</div>
                  <button
                    v-if="isLatest(i)"
                    type="button"
                    class="text-xs text-primary-600 hover:text-primary-700 hover:underline whitespace-nowrap"
                    :disabled="isPending"
                    @click="onSwitchDirection(msg.direction)"
                  >
                    Switch to {{ oppositeOf(msg.direction) }}
                  </button>
                </div>
                <div class="space-y-2">
                  <AskAiCard
                    v-for="card in msg.cards"
                    :key="cardKey(card)"
                    :card="card"
                  />
                </div>
              </div>
            </div>
            </div>
          </TransitionGroup>
        </div>

        <footer class="border-t border-gray-200 px-4 py-3">
          <form class="flex items-end gap-2" @submit.prevent="onSend">
            <textarea
              ref="textareaRef"
              v-model="input"
              rows="2"
              :placeholder="placeholder"
              class="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-gray-50 disabled:text-gray-500"
              :disabled="isPending || direction === null"
              @keydown.enter.exact.prevent="onSend"
              @keydown.esc.prevent="close"
            />
            <Button
              type="submit"
              :disabled="!canSend"
              size="small"
            >
              Send
            </Button>
          </form>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { AskAiCard, AskAiDirection, AskAiMessage } from '~/composables/useAskAiThread'
import { useAuthStore } from '~/store/auth'

const authStore = useAuthStore()
const userInitials = computed(() => {
  const name: string | undefined = authStore.user?.name
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase()
})

const { isOpen, close } = useAskAiModal()
const {
  messages,
  direction,
  load,
  append,
  replaceLast,
  setDirection,
  clear,
  concatenatedUserText,
} = useAskAiThread()
const {
  searchContactsByText,
  searchBusinessesByText,
} = useAiSearch()

const input = ref('')
const isPending = ref(false)
const scrollContainer = ref<HTMLElement | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const placeholder = computed(() =>
  direction.value === null
    ? 'Select an option above to start'
    : 'Describe what you\'re looking for',
)

const canSend = computed(
  () => input.value.trim().length > 0 && !isPending.value && direction.value !== null,
)

const isLatest = (index: number) => index === messages.value.length - 1

const oppositeOf = (d: AskAiDirection): AskAiDirection =>
  d === 'contacts' ? 'businesses' : 'contacts'

const cardKey = (card: AskAiCard) =>
  card.kind === 'contact' ? `c:${card.contactId}` : `b:${card.businessId}`

const scrollToBottom = async () => {
  await nextTick()
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight
  }
}

const ensureGreeting = () => {
  if (messages.value.length === 0 && direction.value === null) {
    append({ role: 'ai', status: 'greeting', createdAt: new Date().toISOString() })
  }
}

watch(isOpen, async (open) => {
  if (open) {
    load()
    ensureGreeting()
    await nextTick()
    textareaRef.value?.focus()
    scrollToBottom()
  }
})

watch(messages, () => {
  scrollToBottom()
}, { deep: true })

const onClear = () => {
  if (isPending.value) return
  clear()
  ensureGreeting()
}

const buildCardsForDirection = (
  d: AskAiDirection,
  contactResults?: { id: string; name: string; aiScore: number; similarity: number; reason: string | null }[],
  businessResults?: { id: string; name: string; aiScore: number; similarity: number; reason: string | null }[],
): AskAiCard[] => {
  if (d === 'contacts') {
    return (contactResults ?? []).map((r) => ({
      kind: 'contact',
      contactId: r.id,
      name: r.name,
      aiScore: r.aiScore ?? 0,
      similarity: r.similarity,
      reason: r.reason ?? null,
    }))
  }
  return (businessResults ?? []).map((r) => ({
    kind: 'business',
    businessId: r.id,
    name: r.name,
    aiScore: r.aiScore ?? 0,
    similarity: r.similarity,
    reason: r.reason ?? null,
  }))
}

const runSearch = async (d: AskAiDirection, queryText: string) => {
  if (queryText.trim().length === 0) return

  // Replace current "searching" placeholder text to reflect direction.
  replaceLast({
    role: 'ai',
    status: 'searching',
    text: d === 'contacts' ? 'Searching contacts...' : 'Searching businesses...',
    createdAt: new Date().toISOString(),
  })

  try {
    let cards: AskAiCard[] = []
    if (d === 'contacts') {
      const res = await searchContactsByText(queryText, { limit: 20 })
      cards = buildCardsForDirection('contacts', res.data, undefined)
    } else {
      const res = await searchBusinessesByText(queryText, { limit: 20 })
      cards = buildCardsForDirection('businesses', undefined, res.data)
    }

    if (cards.length === 0) {
      const emptyMsg: AskAiMessage = {
        role: 'ai',
        status: 'empty',
        text: `No matching ${d} found. Try broadening the description.`,
        createdAt: new Date().toISOString(),
      }
      replaceLast(emptyMsg)
    } else {
      const noun = cards.length === 1 ? d.replace(/s$/, '') : d
      const resultsMsg: AskAiMessage = {
        role: 'ai',
        status: 'results',
        introText: `Found ${cards.length} ${noun}.`,
        direction: d,
        cards,
        createdAt: new Date().toISOString(),
      }
      replaceLast(resultsMsg)
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Connection error. Please try again.'
    replaceLast({
      role: 'ai',
      status: 'error',
      text: message,
      createdAt: new Date().toISOString(),
    })
  }
}

const onPickInitial = (d: AskAiDirection) => {
  if (isPending.value) return
  setDirection(d)
  isPending.value = true
  append({
    role: 'user',
    kind: 'picker',
    text: d === 'businesses' ? 'Businesses' : 'Contacts',
    createdAt: new Date().toISOString(),
  })
  append({
    role: 'ai',
    status: 'typing',
    createdAt: new Date().toISOString(),
  })
  setTimeout(() => {
    replaceLast({
      role: 'ai',
      status: 'empty',
      text: 'Great, tell me more about it.',
      createdAt: new Date().toISOString(),
    })
    isPending.value = false
    nextTick(() => textareaRef.value?.focus())
  }, 900)
}

const onSend = async () => {
  if (!canSend.value || !direction.value) return

  const userText = input.value.trim()
  input.value = ''
  isPending.value = true

  const d = direction.value
  append({ role: 'user', text: userText, createdAt: new Date().toISOString() })
  append({
    role: 'ai',
    status: 'searching',
    text: d === 'contacts' ? 'Searching contacts...' : 'Searching businesses...',
    createdAt: new Date().toISOString(),
  })

  try {
    await runSearch(d, concatenatedUserText.value)
  } finally {
    isPending.value = false
  }
}

const onSwitchDirection = (current: AskAiDirection) => {
  if (isPending.value) return
  const next = oppositeOf(current)
  isPending.value = true
  clear()
  setDirection(next)
  append({
    role: 'ai',
    status: 'typing',
    createdAt: new Date().toISOString(),
  })
  setTimeout(() => {
    replaceLast({
      role: 'ai',
      status: 'empty',
      text: next === 'contacts'
        ? 'Switched to contacts. Tell me what you\'re looking for.'
        : 'Switched to businesses. Tell me what you\'re looking for.',
      createdAt: new Date().toISOString(),
    })
    isPending.value = false
    nextTick(() => textareaRef.value?.focus())
  }, 900)
}
</script>
